import React, {useEffect, useState, useMemo} from 'react';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrButton from '../../../common/arr-button/ArrButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {t} from 'i18next';
import {UseFormSetValue, UseFormWatch, Control} from 'react-hook-form';
import {IMappingResponse} from '../../../../api/types/ingestionApi.type';
import {GridFormValues} from '../gridSchema';
import '../index.scss';
import {FormValues} from '../schema';

interface GridRow {
  sourceField: {value: string; text: string} | null;
  type: {value: string; text: string} | null;
  destinationField: {value: number; text: string} | null;
  mandatory: boolean;
  isNewRow: boolean;
  clientUniqueIdentifierRow: number;
  sourceApiProvided: boolean;
  typeApiProvided: boolean;
  destinationApiProvided: boolean;
}

interface CfmGridProps {
  mappingData: IMappingResponse | null;
  disabled?: boolean;
  _gridControl: Control<GridFormValues>;
  gridSetValue: UseFormSetValue<GridFormValues>;
  _gridWatch: UseFormWatch<GridFormValues>;
  formValues: FormValues;
  isLeftLock: boolean;
}

/**
 * Builds a descriptive aria-label for a mapping row.
 * e.g. "Row 3: SourceDocID — DOCPBASIC — SourceDocID, mandatory"
 * or   "Row 5: unmapped, optional"
 */
const buildRowAriaLabel = (row: GridRow, idx: number): string => {
  const rowNumber = idx + 1;
  const sourceText =
    row.sourceField?.text ??
    t('ingestions.Wizard.ariaLabels.cfmGrid.unmapped') ??
    'unmapped';
  const typeText =
    row.type?.text ??
    t('ingestions.Wizard.ariaLabels.cfmGrid.unmapped') ??
    'unmapped';
  const destText =
    row.destinationField?.text ??
    t('ingestions.Wizard.ariaLabels.cfmGrid.unmapped') ??
    'unmapped';
  const mandatoryText = row.mandatory
    ? (t('ingestions.Wizard.ariaLabels.cfmGrid.required') ?? 'required')
    : (t('ingestions.Wizard.ariaLabels.cfmGrid.optional') ?? 'optional');

  return `${t('ingestions.Wizard.ariaLabels.cfmGrid.rowLabel') ?? 'Row'} ${rowNumber}: ${sourceText} — ${typeText} — ${destText}, ${mandatoryText}`;
};

const CfmGrid: React.FC<CfmGridProps> = ({
  mappingData,
  disabled = false,
  _gridControl,
  gridSetValue,
  _gridWatch,
  formValues,
  isLeftLock,
}) => {
  const [rows, setRows] = useState<GridRow[]>([]);
  const [nextClientID, setNextClientID] = useState(1000);

  // show section when ingestionType is Overlay only and Dat load file is not selected
  const isShowingDatErrorGrid =
    (formValues.ingestionType?.key === '2' || formValues.ingestionType?.key === 'Overlay') && !formValues.sources.dat.enabled;

  // Sort source field options alphabetically
  const sourceFieldOptions = useMemo(() => {
    if (!mappingData?.sourceField) return [];

    return [...mappingData.sourceField].sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }, [mappingData?.sourceField]);

  // Sort type options alphabetically
  const typeOptions = useMemo(() => {
    if (!mappingData?.type) return [];

    return [...mappingData.type].sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }, [mappingData?.type]);

  // Function to get sorted destination options
  const getDestinationOptions = (typeID: string | null) => {
    if (!typeID || typeID === 'Select' || !mappingData) return [];
    const selectedType = mappingData.type.find((t) => t.value === typeID);
    if (!selectedType?.destinationField) return [];

    return [...selectedType.destinationField].sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  };

  // Initialize rows from API response
  useEffect(() => {
    if (!mappingData) return;

    const initialRows = mappingData.rowData.map((rowData) => {
      const sourceField =
        mappingData.sourceField.find(
          (sf) => Number(sf.value) === rowData.selectedSourceFieldID
        ) || null;

      const type =
        mappingData.type.find((t) => t.value === rowData.selectedTypeID) ||
        null;

      let destinationField = null;
      if (type && rowData.selectedDestinationID !== 0) {
        destinationField =
          type.destinationField.find(
            (df) => df.value === rowData.selectedDestinationID
          ) || null;
      }

      return {
        sourceField,
        type,
        destinationField,
        mandatory: rowData.mandatory,
        isNewRow: rowData.isNewRow,
        clientUniqueIdentifierRow: rowData.clientUniqueIdentifierRow,
        sourceApiProvided: !!sourceField,
        typeApiProvided: !!type,
        destinationApiProvided: !!destinationField,
      };
    });

    setRows(initialRows);
    syncToFormState(initialRows);
  }, [mappingData]);

  // Sync rows to form state
  const syncToFormState = (currentRows: GridRow[]) => {
    const mappings = currentRows.map((row) => ({
      sourceFieldID: row.sourceField ? Number(row.sourceField.value) : null,
      sourceFieldText: row.sourceField?.text || null,
      typeID: row.type?.value || null,
      typeText: row.type?.text || null,
      destinationID: row.destinationField?.value || null,
      destinationText: row.destinationField?.text || null,
      mandatory: row.mandatory,
      isNewRow: row.isNewRow,
      clientUniqueIdentifierRow: row.clientUniqueIdentifierRow,
    }));

    gridSetValue('columnMappings', mappings, {shouldDirty: true});
  };

  const handleAddRow = () => {
    const newRow: GridRow = {
      sourceField: null,
      type: null,
      destinationField: null,
      mandatory: false,
      isNewRow: true,
      clientUniqueIdentifierRow: nextClientID,
      sourceApiProvided: false,
      typeApiProvided: false,
      destinationApiProvided: false,
    };
    const updated = [...rows, newRow];
    setRows(updated);
    setNextClientID(nextClientID + 1);
    syncToFormState(updated);
  };

  const handleDelete = (rowIdx: number) => {
    const updated = rows.filter((_, i) => i !== rowIdx);
    setRows(updated);
    syncToFormState(updated);
  };

  const handleSourceFieldChange = (rowIdx: number, val: any) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[rowIdx].sourceField = val;

      // Only clear Type and Destination for non-mandatory rows
      if (!copy[rowIdx].mandatory) {
        copy[rowIdx].type = null;
        copy[rowIdx].destinationField = null;
      }

      return copy;
    });
    setTimeout(() => syncToFormState(rows), 0);
  };

  const handleTypeChange = (rowIdx: number, val: any) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[rowIdx].type = val;

      // Only clear Destination for non-mandatory rows
      if (!copy[rowIdx].mandatory) {
        copy[rowIdx].destinationField = null;
      }

      return copy;
    });
    setTimeout(() => syncToFormState(rows), 0);
  };

  const handleDestinationChange = (rowIdx: number, val: any) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[rowIdx].destinationField = val;
      return copy;
    });
    setTimeout(() => syncToFormState(rows), 0);
  };

  return (
    <Box
      className="cfm-grid"
      component="section"
      role="region"
      aria-labelledby="cfm-section-title"
      aria-describedby="cfm-description cfm-denotes"
    >
      {/* 17. Mapping Table (focusable region) */}
      <Box
        role="table"
        aria-label={
          t('ingestions.Wizard.ariaLabels.cfmGrid.mappingTable') ||
          'Field mapping table'
        }
      >
        {/* Column Headers */}
        <Box
          className="cfm-grid-header"
          role="row"
          aria-label={t('ingestions.Wizard.ariaLabels.cfmGrid.columnHeaders')}
        >
          <Typography
            className="cfm-grid-header-text"
            role="columnheader"
            tabIndex={0}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.cfmGrid.sourceFieldColumn'
            )}
            data-testid={`cfm-grid-col1name`}
          >
            {t('ingestions.Wizard.cfm.Col1Name')}
          </Typography>
          <Typography
            className="cfm-grid-header-text"
            role="columnheader"
            tabIndex={0}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.cfmGrid.fieldCategoryColumn'
            )}
            data-testid={`cfm-grid-col2name`}
          >
            {t('ingestions.Wizard.cfm.Col2Name')}
          </Typography>
          <Typography
            className="cfm-grid-header-text"
            role="columnheader"
            tabIndex={0}
            aria-label={t(
              'ingestions.Wizard.ariaLabels.cfmGrid.destinationFieldColumn'
            )}
            onKeyDown={(e: any) => {
              if (isLeftLock) return;
              if (e.key === 'Tab' && e.shiftKey) return;
              if (e.key === 'Tab' && !isShowingDatErrorGrid) {
                e.preventDefault();
                (document.getElementById('page-title') as HTMLElement)?.focus();
              }
            }}
            data-testid={`cfm-grid-col3name`}
          >
            {t('ingestions.Wizard.cfm.Col3Name')}
          </Typography>
        </Box>

        {/* Rows with descriptive aria-labels */}
        {!isShowingDatErrorGrid &&
          rows.map((row, idx) => {
            const destinationOptions = getDestinationOptions(
              row.type?.value || null
            );
            const showDelete = row.isNewRow;
            const rowAriaLabel = buildRowAriaLabel(row, idx);

            let isSourceFieldDisabled = disabled;
            let isTypeDisabled = disabled;
            let isDestinationDisabled = disabled;

            if (row.mandatory) {
              if (row.sourceApiProvided && idx === 0) {
                isSourceFieldDisabled = true;
              } else {
                isSourceFieldDisabled = disabled;
              }

              if (row.typeApiProvided) {
                isTypeDisabled = true;
              } else {
                const sourceSelected = row.sourceField !== null;
                isTypeDisabled = disabled || !sourceSelected;
              }

              if (row.destinationApiProvided) {
                isDestinationDisabled = true;
              } else {
                const typeSelected =
                  row.type !== null && row.type.value !== 'Select';
                isDestinationDisabled = disabled || !typeSelected;
              }
            } else {
              isSourceFieldDisabled = disabled;
              isTypeDisabled = disabled;
              isDestinationDisabled = disabled;
            }

            const ariaLabelBase = t(
              'ingestions.Wizard.ariaLabels.cfmGrid.sourceFieldDropdown'
            );
            const ariaLabelIndex = idx + 1;
            const ariaLabelRequired = row.mandatory
              ? `, ${t('ingestions.Wizard.ariaLabels.cfmGrid.required')}`
              : '';

            const ariaLabelBaseType = t(
              'ingestions.Wizard.ariaLabels.cfmGrid.typeDropdown'
            );
            const ariaLabelRequiredType = row.mandatory
              ? `, ${t('ingestions.Wizard.ariaLabels.cfmGrid.required')}`
              : '';

            const ariaLabelBaseDestination = t(
              'ingestions.Wizard.ariaLabels.cfmGrid.destinationFieldDropdown'
            );
            const ariaLabelRequiredDestination = row.mandatory
              ? `, ${t('ingestions.Wizard.ariaLabels.cfmGrid.required')}`
              : '';

            return (
              <Box
                key={`${row.clientUniqueIdentifierRow}-${idx}`}
                className="cfm-grid-row"
                role="row"
                aria-label={rowAriaLabel}
                aria-describedby={`row-${idx}-description`}
              >
                {/* Source Field */}
                <ArrSingleSelectDropdown
                  dropdownLabel={`${t('ingestions.Wizard.cfmGrid.sourceField')} ${idx + 1}`}
                  sizeVariant="large"
                  dropdownItems={sourceFieldOptions}
                  searchable
                  dropdownItemDisplay="text"
                  dropdownItemKey="value"
                  selectedValues={row.sourceField}
                  onSelectionChange={(val) => handleSourceFieldChange(idx, val)}
                  customWidth={13}
                  disabled={isSourceFieldDisabled}
                  error={row.mandatory && !row.sourceField}
                  errorText={
                    row.mandatory && !row.sourceField ? 'Required' : undefined
                  }
                  dataTestId={`asd-cfm-source-field-dropdown-${idx}`}
                  selectRestProps={{
                    'aria-label':
                      ariaLabelBase + ' ' + ariaLabelIndex + ariaLabelRequired,
                    'aria-required': row.mandatory ? 'true' : 'false',
                    'aria-invalid': row.mandatory && !row.sourceField,
                    role: 'gridcell',
                  }}
                  tabIndex={-1}
                />
                {/* Type (Field Category) */}
                <ArrSingleSelectDropdown
                  dropdownLabel={`${t('ingestions.Wizard.cfmGrid.type')} ${idx + 1}`}
                  sizeVariant="large"
                  searchable
                  dropdownItems={typeOptions}
                  dropdownItemDisplay="text"
                  dropdownItemKey="value"
                  selectedValues={row.type}
                  onSelectionChange={(val) => handleTypeChange(idx, val)}
                  customWidth={13}
                  disabled={isTypeDisabled}
                  error={
                    row.mandatory && (!row.type || row.type.value === 'Select')
                  }
                  errorText={
                    row.mandatory && (!row.type || row.type.value === 'Select')
                      ? 'Required'
                      : undefined
                  }
                  dataTestId={`asd-cfm-type-dropdown-${idx}`}
                  selectRestProps={{
                    'aria-label': `${ariaLabelBaseType} ${idx + 1}${ariaLabelRequiredType}`,
                    'aria-required': row.mandatory ? 'true' : 'false',
                    'aria-invalid':
                      row.mandatory &&
                      (!row.type || row.type.value === 'Select'),
                    role: 'gridcell',
                  }}
                  tabIndex={-1}
                />
                {/* Destination Field */}
                <ArrSingleSelectDropdown
                  dropdownLabel={`${t('ingestions.Wizard.cfmGrid.destinationField')} ${idx + 1}`}
                  sizeVariant="large"
                  searchable
                  dropdownItems={destinationOptions}
                  dropdownItemDisplay="text"
                  dropdownItemKey="value"
                  selectedValues={row.destinationField}
                  onSelectionChange={(val) => handleDestinationChange(idx, val)}
                  customWidth={13}
                  disabled={isDestinationDisabled}
                  error={
                    row.mandatory &&
                    (!row.destinationField || row.destinationField.value === 0)
                  }
                  errorText={
                    row.mandatory &&
                    (!row.destinationField || row.destinationField.value === 0)
                      ? 'Required'
                      : undefined
                  }
                  dataTestId={`asd-cfm-destination-field-dropdown-${idx}`}
                  selectRestProps={{
                    'aria-label': `${ariaLabelBaseDestination} ${idx + 1}${ariaLabelRequiredDestination}`,
                    'aria-required': row.mandatory ? 'true' : 'false',
                    'aria-invalid':
                      row.mandatory &&
                      (!row.destinationField ||
                        row.destinationField.value === 0),
                    role: 'gridcell',
                  }}
                  tabIndex={-1}
                />
                {/* Delete button (only for user-added rows) */}
                {showDelete && (
                  <Tooltip title={t('common.delete') || 'Delete'}>
                    <IconButton
                      aria-label={`${t('ingestions.Wizard.ariaLabels.cfmGrid.deleteButton')} ${idx + 1}`}
                      size="small"
                      className="cfm-grid-row-delete-btn"
                      onClick={() => handleDelete(idx)}
                      disabled={disabled}
                      tabIndex={0}
                      data-testid={`cfm-grid-${idx}-delete`}
                    >
                      <DeleteOutlineIcon
                        className="delete-icon"
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {!showDelete && <Box />}
              </Box>
            );
          })}
      </Box>
      {isShowingDatErrorGrid && !disabled && (
        <Box className="cfm-grid-dat-error-message" role="alert" tabIndex={0}>
          <Typography
            className="typo-text body3_size"
            data-testid={`cfm-grid-no-mappings-dat-error1`}
          >
            {t('ingestions.Wizard.cfm.noMappingsDatError1')}
          </Typography>
          <Typography
            className="typo-text body3_size"
            data-testid={`cfm-grid-no-mappings-dat-error2`}
          >
            {t('ingestions.Wizard.cfm.noMappingsDatError2')}
          </Typography>
        </Box>
      )}
      {/* 18. Add Button (adds new field mapping row) */}
      <Box className="cfm-grid-btn-container">
        <ArrButton
          variant="primary"
          size="small"
          type="button"
          onClick={handleAddRow}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && e.shiftKey) return;
            if (e.key === 'Tab') {
              e.preventDefault();
              (document.getElementById('page-title') as HTMLElement)?.focus();
            }
          }}
          disabled={disabled || isShowingDatErrorGrid}
          tabIndex={0}
          aria-label={t('ingestions.Wizard.ariaLabels.cfmGrid.addButton')}
          dataTestId="add-mapping-row-button"
        >
          {t('ingestions.Wizard.cfm.btnAdd')}
        </ArrButton>
      </Box>
    </Box>
  );
};

export default CfmGrid;
